import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { NexusProvider } from '@azyntor-labs/nexus-auth';
import { LoadingScreen } from '@azyntor-labs/nexus-ui';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorHandler } from './components/ErrorHandler';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useExamAuth } from '@/hooks/useExamAuth';

// Nexus Auth SSO Configuration
import { NEXUS_AUTH_CONFIG } from './config/nexusAuth';

// Layouts
import { DashboardLayout } from './components/layout/DashboardLayout';
import { SystemAdminLayout } from './components/layout/SystemAdminLayout';

// Nexus SDK v4 configuration — cookie-based auth, no device headers, no
// localStorage. The SDK owns all token state in memory.
const nexusConfig = {
  apiUrl:
    import.meta.env.VITE_NEXUS_AUTH_API_URL ||
    'http://auth.azyntorlabs.local:1201',
  webUrl:
    import.meta.env.VITE_NEXUS_AUTH_WEB_URL ||
    'http://auth.azyntorlabs.local:1200',
  serviceName: import.meta.env.VITE_SERVICE_NAME || 'exam',
  broadcastLogout: true,
  debug: import.meta.env.DEV,
};

/**
 * Redirect to centralized Nexus Auth login
 * Uses SSO flow - redirects to Nexus Auth with service branding and return URL
 *
 * IMPORTANT: Waits for Nexus SDK to finish checking SSO session before redirecting
 * This prevents redirect loops when returning from Nexus Auth login
 */
function RedirectToLogin() {
  const location = useLocation();
  const { isLoading, isAuthenticated } = useExamAuth();
  const [hasWaited, setHasWaited] = useState(false);

  useEffect(() => {
    // Wait for Nexus SDK to finish loading before making redirect decision
    if (isLoading) {
      return;
    }

    // If authenticated, redirect to dashboard based on role
    if (isAuthenticated) {
      window.location.href = '/';
      return;
    }

    // Add a small delay to allow session cookie to be checked
    if (!hasWaited) {
      const timeout = setTimeout(() => setHasWaited(true), 2000);
      return () => clearTimeout(timeout);
    }

    // Only redirect after we've confirmed no session
    const returnUrl = window.location.origin + location.pathname + location.search;
    NEXUS_AUTH_CONFIG.redirectToLogin(returnUrl);
  }, [location, isLoading, isAuthenticated, hasWaited]);

  return <LoadingScreen message="Checking authentication..." variant="cyber" />;
}

/**
 * Redirect to centralized Nexus Auth register
 */
function RedirectToRegister() {
  useEffect(() => {
    const returnUrl = window.location.origin + '/auth/callback';
    NEXUS_AUTH_CONFIG.redirectToRegister(returnUrl);
  }, []);

  return <LoadingScreen message="Redirecting to registration..." variant="cyber" />;
}

/**
 * Redirect to centralized Nexus Auth forgot password
 */
function RedirectToForgotPassword() {
  useEffect(() => {
    const returnUrl = window.location.origin + '/auth/callback';
    window.location.href = NEXUS_AUTH_CONFIG.getForgotPasswordUrl(returnUrl);
  }, []);

  return <LoadingScreen message="Redirecting..." variant="cyber" />;
}

// SSO Auth Callback Page
import { AuthCallback } from './pages/auth/AuthCallback';

// Admin Pages (Tenant Admin)
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { UserManagement } from './pages/admin/UserManagement';

// System Admin Pages
import { SystemAdminDashboard, SystemAdminTenantManagement } from './pages/system-admin';

// Teacher Pages
import { TeacherDashboard } from './pages/teacher/TeacherDashboard';
import { ExamManagement } from './pages/teacher/ExamManagement';
import { ExamEditor } from './pages/teacher/ExamEditor';
import { AddQuestionsFromBank } from './pages/teacher/AddQuestionsFromBank';
import { ExamEnrollmentPage } from './pages/teacher/ExamEnrollmentPage';
import { StudentGroupsPage } from './pages/teacher/StudentGroupsPage';
import { QuestionBankManagement } from './pages/teacher/QuestionBankManagement';
import { QuestionBankDetails } from './pages/teacher/QuestionBankDetails';
import { QuestionManagement } from './pages/teacher/QuestionManagement';
import { QuestionEdit } from './pages/teacher/QuestionEdit';
import { QuestionsPage } from './pages/teacher/QuestionsPage';
import { GradingPage } from './pages/teacher/GradingPage';
import { GradebookPage } from './pages/teacher/GradebookPage';
import { StudentGradeDetailPage } from './pages/teacher/StudentGradeDetailPage';
import { QuestionImportExportPage } from './pages/teacher/QuestionImportExportPage';
import { LiveExamMonitorPage } from './pages/teacher/LiveExamMonitorPage';
import { QuestionBankSharingPage } from './pages/teacher/QuestionBankSharingPage';
import { CertificateManagementPage } from './pages/teacher/CertificateManagementPage';
import { ExamResultsDetailPage } from './pages/teacher/ExamResultsDetailPage';
import { AttemptDetailPage } from './pages/teacher/AttemptDetailPage';
import { SuspiciousAttemptsPage } from './pages/teacher/SuspiciousAttemptsPage';
import { ExamTemplatesPage } from './pages/teacher/ExamTemplatesPage';
import { ExamTemplateEditorPage } from './pages/teacher/ExamTemplateEditorPage';
import { StudentProgressPage } from './pages/teacher/StudentProgressPage';

// New Feature Pages
const ViolationReviewPage = lazy(() => import('./pages/teacher/ViolationReviewPage').then(m => ({ default: m.ViolationReviewPage })));
const ProctoringEvidencePage = lazy(() => import('./pages/teacher/ProctoringEvidencePage').then(m => ({ default: m.ProctoringEvidencePage })));
const PlagiarismReviewPage = lazy(() => import('./pages/teacher/PlagiarismReviewPage').then(m => ({ default: m.PlagiarismReviewPage })));
const PlagiarismResultDetailPage = lazy(() => import('./pages/teacher/PlagiarismResultDetailPage').then(m => ({ default: m.PlagiarismResultDetailPage })));
const StudentViolationDetailPage = lazy(() => import('./pages/teacher/StudentViolationDetailPage').then(m => ({ default: m.StudentViolationDetailPage })));
const PartnerPortalPage = lazy(() => import('./pages/admin/PartnerPortalPage').then(m => ({ default: m.PartnerPortalPage })));
const AIModelConfigPage = lazy(() => import('./pages/admin/AIModelConfigPage').then(m => ({ default: m.AIModelConfigPage })));
const CertificateTemplatesPage = lazy(() => import('./pages/admin/CertificateTemplatesPage').then(m => ({ default: m.CertificateTemplatesPage })));
const CertificateVerifyPage = lazy(() => import('./pages/certificates/CertificateVerifyPage').then(m => ({ default: m.CertificateVerifyPage })));

// Admin Pages (new)
import { AuditLogsPage } from './pages/admin/AuditLogsPage';
import { PermissionTestPage } from './pages/admin/PermissionTestPage';
import { ReportsPage } from './pages/admin/ReportsPage';
import { SystemSettingsPage } from './pages/admin/SystemSettingsPage';
import { BulkUserOperationsPage } from './pages/admin/BulkUserOperationsPage';
import { NotificationAdminPage } from './pages/admin/NotificationAdminPage';

// System Admin Pages (new)
import { BackupManagementPage } from './pages/system-admin/BackupManagementPage';
import { SystemHealthPage } from './pages/system-admin/SystemHealthPage';
import { ActivityLogsPage } from './pages/system-admin/ActivityLogsPage';
import { BillingPage } from './pages/system-admin/BillingPage';

// Student Pages
import { StudentDashboard } from './pages/student/StudentDashboard';
// Lazy load ExamTaking - it includes heavy AI/TensorFlow proctoring code
const ExamTaking = lazy(() => import('./pages/student/ExamTaking').then(m => ({ default: m.ExamTaking })));
import { EnrollWithCode } from './pages/student/EnrollWithCode';
import { ExamResults } from './pages/student/ExamResults';
import { ExamTerminated } from './pages/student/ExamTerminated';

// Analytics Pages
import { AnalyticsDashboard } from './pages/analytics/AnalyticsDashboard';
import { CampaignAnalyticsDashboard } from './pages/analytics/CampaignAnalyticsDashboard';
import { ExamAnalyticsPage } from './pages/analytics/ExamAnalyticsPage';
import { StudentAnalyticsPage } from './pages/analytics/StudentAnalyticsPage';
import { EnhancedAnalyticsDashboard } from './pages/analytics/EnhancedAnalyticsDashboard';

// Proctoring Pages - Lazy loaded due to heavy AI dependencies
const ProctoringMonitor = lazy(() => import('./pages/proctoring/ProctoringMonitor').then(m => ({ default: m.ProctoringMonitor })));

// Profile Page
import { ProfilePage } from './pages/ProfilePage';

// Notifications & Announcements Pages
import { NotificationsPage } from './pages/notifications/NotificationsPage';
import { AnnouncementsPage } from './pages/announcements/AnnouncementsPage';

// Certificates Pages
import { MyCertificatesPage } from './pages/certificates/MyCertificatesPage';
import { BrowseExamsPage } from './pages/student/BrowseExamsPage';
import { AITestPage } from './pages/student/AITestPage';

// Test Dashboard Pages
import { TestDashboardPage } from './pages/TestDashboardPage';
import { TestRunDetailsPage } from './pages/TestRunDetailsPage';

// UI Demo Pages
import { AccessDenied } from './pages/ui-pages/AccessDenied';
import { ServerError } from './pages/ui-pages/ServerError';

// Public Pages
import { EnrollViaMagicLink } from './pages/public/EnrollViaMagicLink';

// Shared Pages
import { SharedQuestionBankPage } from './pages/shared/SharedQuestionBankPage';
import { NotFound } from './pages/ui-pages/NotFound';
import { ComponentShowcase } from './pages/ui-pages/ComponentShowcase';
import { NewComponentShowcase } from './pages/ui-pages/NewComponentShowcase';
import { LoadingScreenDemo } from './pages/ui-pages/LoadingScreenDemo';
import { ComingSoon } from './pages/ui-pages/ComingSoon';
import { ComingSoonSimple } from './pages/ui-pages/ComingSoonSimple';

// Test Pages
import { AIProctorTest } from './pages/AIProctorTest';

/**
 * Check if the given permissions array contains the global wildcard.
 * v4: derived from the SDK's in-memory auth state, never from localStorage.
 */
function hasGlobalSuperuserPermission(permissions: string[]): boolean {
  return permissions.includes('*:*:*') || permissions.includes('*:*:*:*');
}

/**
 * Check if the given roles array contains a global-admin role.
 * v4: derived from the SDK's in-memory auth state, never from localStorage.
 */
function hasGlobalAdminRole(roles: string[]): boolean {
  return roles.includes('system_admin') || roles.includes('super_admin');
}

/**
 * Custom protected route for admin pages that also grants access to global superusers
 * This bypasses Nexus SDK's role check for users with *:*:* permission
 */
function AdminProtectedRoute({ children, roles = [] }: { children: React.ReactNode; roles?: string[] }) {
  const { isAuthenticated, roles: userRoles, permissions: userPerms } = useExamAuth();

  if (!isAuthenticated) {
    return <RedirectToLogin />;
  }

  // CRITICAL: If authenticated but roles not loaded yet, wait
  if (isAuthenticated && (!userRoles || userRoles.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Grant access to global superusers (users with *:*:* permission in any service)
  if (hasGlobalSuperuserPermission(userPerms) || hasGlobalAdminRole(userRoles)) {
    return <>{children}</>;
  }

  // Direct role check — check if user has any of the required roles
  if (roles.length > 0 && userRoles && userRoles.length > 0) {
    const hasRequiredRole = roles.some(role => userRoles.includes(role));
    if (hasRequiredRole) {
      return <>{children}</>;
    }
    return <AccessDenied />;
  }

  // Fall back to the local ProtectedRoute
  return (
    <ProtectedRoute roles={roles} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}>
      {children}
    </ProtectedRoute>
  );
}

// Dashboard redirect component
function DashboardRedirect() {
  const { isAuthenticated, isLoading, hasAnyRole, roles, permissions: userPerms } = useExamAuth();

  // Wait for Nexus SDK to finish loading (SSO check)
  if (isLoading) {
    return <LoadingScreen message="Checking authentication..." variant="cyber" />;
  }

  if (!isAuthenticated) {
    return <RedirectToLogin />;
  }

  // CRITICAL: If authenticated but roles not loaded yet, wait
  // This handles race condition where Zustand hydrates before roles are extracted from JWT
  if (isAuthenticated && (!roles || roles.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for global superuser or system_admin - redirect to System Admin dashboard
  if (hasGlobalSuperuserPermission(userPerms) || hasGlobalAdminRole(roles)) {
    return <Navigate to="/system-admin/dashboard" replace />;
  }

  // Check for super_admin role in exam service - redirect to System Admin dashboard
  if (hasAnyRole(['system_admin', 'super_admin'])) {
    return <Navigate to="/system-admin/dashboard" replace />;
  }

  // Tenant admin - redirect to tenant admin dashboard
  if (hasAnyRole(['admin', 'tenant_admin'])) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Teachers/Instructors
  if (hasAnyRole(['instructor', 'teacher'])) {
    return <Navigate to="/teacher/dashboard" replace />;
  }

  // Default to student dashboard
  return <Navigate to="/student/dashboard" replace />;
}

// Inner app component that uses auth hooks
function AppRoutes() {
  return (
    <Routes>
      {/* SSO Auth Callback - handles redirect from Nexus Auth after login */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* SSO Redirects - redirect to centralized Nexus Auth */}
      <Route path="/login" element={<RedirectToLogin />} />
      <Route path="/register" element={<RedirectToRegister />} />
      <Route path="/forgot-password" element={<RedirectToForgotPassword />} />

      {/* Public Enrollment via Magic Link (no layout) */}
      <Route path="/enroll/:token" element={<EnrollViaMagicLink />} />

      {/* Public Certificate Verification (no layout, no auth) */}
      <Route path="/verify/:code" element={<Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><CertificateVerifyPage /></Suspense>} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}><DashboardLayout /></ProtectedRoute>}>
        {/* Dashboard redirect based on role */}
        <Route path="/" element={<DashboardRedirect />} />

        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />

        {/* Notifications - accessible to all authenticated users */}
        <Route path="/notifications" element={<NotificationsPage />} />

        {/* Shared Question Bank - accessible to all authenticated users */}
        <Route path="/shared/question-bank/:token" element={<SharedQuestionBankPage />} />

        {/* Admin Routes - Tenant Admin only (system admins use /system-admin/*) */}
        {/* Using ProtectedRoute directly to prevent global superusers from accessing tenant admin */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute roles={['admin', 'tenant_admin']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<Navigate to="/system-admin/dashboard" replace />}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute roles={['admin', 'tenant_admin']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<Navigate to="/system-admin/dashboard" replace />}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute roles={['admin', 'tenant_admin']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<Navigate to="/system-admin/dashboard" replace />}>
              <AnnouncementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}>
              <AuditLogsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/permissions"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'instructor', 'teacher']}>
              <PermissionTestPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}>
              <ReportsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/bulk-users"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}>
              <BulkUserOperationsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}>
              <SystemSettingsPage />
            </AdminProtectedRoute>
          }
        />
        <Route path="/admin/notifications" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}><NotificationAdminPage /></AdminProtectedRoute>} />
        <Route path="/admin/partners" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><PartnerPortalPage /></Suspense></AdminProtectedRoute>} />
        <Route path="/admin/ai-models" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><AIModelConfigPage /></Suspense></AdminProtectedRoute>} />
        <Route path="/admin/certificate-templates" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><CertificateTemplatesPage /></Suspense></AdminProtectedRoute>} />

        {/* Teacher Routes - using AdminProtectedRoute to allow global superusers */}
        <Route
          path="/teacher/dashboard"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <TeacherDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/exams"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <ExamManagement />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/exams/create"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <ExamEditor />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/exams/:examId/edit"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <ExamEditor />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/exams/:examId/add-questions"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <AddQuestionsFromBank />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/exams/:examId/view"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <ExamEditor viewOnly />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/exams/:examId/enrollment"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <ExamEnrollmentPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/groups"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <StudentGroupsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/question-banks"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <QuestionBankManagement />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/question-banks/:bankId"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <QuestionBankDetails />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/question-banks/:bankId/questions"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <QuestionManagement />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/question-banks/:bankId/questions/:questionId/edit"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <QuestionEdit />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/questions"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <QuestionsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/grading"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <GradingPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/grading/:attemptId"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <GradingPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/gradebook"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <GradebookPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/gradebook/:attemptId"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <StudentGradeDetailPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/question-import-export"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <QuestionImportExportPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/teacher/live-monitor"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher', 'proctor']}>
              <LiveExamMonitorPage />
            </AdminProtectedRoute>
          }
        />
        <Route path="/teacher/announcements" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><AnnouncementsPage /></AdminProtectedRoute>} />
        <Route path="/teacher/question-bank-sharing" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><QuestionBankSharingPage /></AdminProtectedRoute>} />
        <Route path="/teacher/certificates" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><CertificateManagementPage /></AdminProtectedRoute>} />
        <Route path="/teacher/exam-results" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><ExamResultsDetailPage /></AdminProtectedRoute>} />
        <Route path="/teacher/exam-results/exam/:examId" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><ExamResultsDetailPage /></AdminProtectedRoute>} />
        <Route path="/teacher/exam-results/attempt/:attemptId" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><AttemptDetailPage /></AdminProtectedRoute>} />
        <Route path="/teacher/exams/:examId/suspicious-attempts" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><SuspiciousAttemptsPage /></AdminProtectedRoute>} />
        <Route path="/teacher/templates" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><ExamTemplatesPage /></AdminProtectedRoute>} />
        <Route path="/teacher/templates/create" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><ExamTemplateEditorPage /></AdminProtectedRoute>} />
        <Route path="/teacher/templates/:templateId/edit" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><ExamTemplateEditorPage /></AdminProtectedRoute>} />
        <Route path="/teacher/student-progress" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><StudentProgressPage /></AdminProtectedRoute>} />
        <Route path="/teacher/violation-review" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher', 'proctor']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><ViolationReviewPage /></Suspense></AdminProtectedRoute>} />
        <Route path="/teacher/violation-review/:examId" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher', 'proctor']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><ViolationReviewPage /></Suspense></AdminProtectedRoute>} />
        <Route path="/teacher/violation-review/:examId/student/:studentId" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher', 'proctor']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><StudentViolationDetailPage /></Suspense></AdminProtectedRoute>} />
        <Route path="/teacher/proctoring-evidence/:examId/:attemptId" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher', 'proctor']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><ProctoringEvidencePage /></Suspense></AdminProtectedRoute>} />
        <Route path="/teacher/plagiarism" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><PlagiarismReviewPage /></Suspense></AdminProtectedRoute>} />
        <Route path="/teacher/plagiarism/:resultId" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><Suspense fallback={<LoadingScreen message="Loading..." variant="cyber" />}><PlagiarismResultDetailPage /></Suspense></AdminProtectedRoute>} />

        {/* Student Routes - Allow any authenticated user for now until roles are fixed */}
        <Route
          path="/student/dashboard"
          element={
            <StudentDashboard />
          }
        />
        <Route
          path="/student/enroll-with-code"
          element={
            <ProtectedRoute roles={['student']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}>
              <EnrollWithCode />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/exams/:id/take"
          element={
            <ProtectedRoute roles={['student']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}>
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen message="Loading exam..." variant="cyber" />}>
                  <ExamTaking />
                </Suspense>
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/results"
          element={
            <ProtectedRoute roles={['student']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}>
              <ExamResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/results/:attemptId"
          element={
            <ProtectedRoute roles={['student']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}>
              <ExamResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/exam-terminated"
          element={
            <ProtectedRoute roles={['student']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}>
              <ExamTerminated />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student/certificates"
          element={
            <ProtectedRoute roles={['student']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}>
              <MyCertificatesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/student/browse-exams" element={<ProtectedRoute roles={['student']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}><BrowseExamsPage /></ProtectedRoute>} />
        <Route path="/student/ai-test" element={<ProtectedRoute roles={['student', 'instructor', 'admin', 'super_admin', 'system_admin']} unauthenticatedComponent={<RedirectToLogin />} unauthorizedComponent={<AccessDenied />}><AITestPage /></ProtectedRoute>} />

        {/* Analytics Routes - using AdminProtectedRoute to allow global superusers */}
        <Route
          path="/analytics"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <AnalyticsDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/analytics/campaigns"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <CampaignAnalyticsDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/analytics/exams/:id"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <ExamAnalyticsPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/analytics/students/:id"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}>
              <StudentAnalyticsPage />
            </AdminProtectedRoute>
          }
        />
        <Route path="/analytics/enhanced" element={<AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher']}><EnhancedAnalyticsDashboard /></AdminProtectedRoute>} />

        {/* Proctoring Routes - using AdminProtectedRoute to allow global superusers */}
        <Route
          path="/proctoring/monitor"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'tenant_admin', 'instructor', 'teacher', 'proctor']}>
              <ErrorBoundary>
                <Suspense fallback={<LoadingScreen message="Loading proctoring monitor..." variant="cyber" />}>
                  <ProctoringMonitor />
                </Suspense>
              </ErrorBoundary>
            </AdminProtectedRoute>
          }
        />

        {/* Test Dashboard Routes - using AdminProtectedRoute to allow global superusers */}
        <Route
          path="/tests"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'instructor', 'teacher']}>
              <TestDashboardPage />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/tests/runs/:runId"
          element={
            <AdminProtectedRoute roles={['super_admin', 'admin', 'instructor', 'teacher']}>
              <TestRunDetailsPage />
            </AdminProtectedRoute>
          }
        />
      </Route>

      {/* System Admin Routes - separate layout with tenant selector */}
      <Route element={
        <AdminProtectedRoute roles={['system_admin', 'super_admin']}>
          <SystemAdminLayout />
        </AdminProtectedRoute>
      }>
        <Route path="/system-admin/dashboard" element={<SystemAdminDashboard />} />
        <Route path="/system-admin/tenants" element={<SystemAdminTenantManagement />} />
        <Route path="/system-admin/users" element={<UserManagement />} />
        <Route path="/system-admin/analytics" element={<AnalyticsDashboard />} />
        <Route path="/system-admin/announcements" element={<AnnouncementsPage />} />
        <Route path="/system-admin/activity" element={<ActivityLogsPage />} />
        <Route path="/system-admin/billing" element={<BillingPage />} />
        <Route path="/system-admin/settings" element={<SystemSettingsPage />} />
        <Route path="/system-admin/backups" element={<BackupManagementPage />} />
        <Route path="/system-admin/system-health" element={<SystemHealthPage />} />
      </Route>

      {/* Component Showcase */}
      <Route path="/showcase" element={<NewComponentShowcase />} />
      <Route path="/showcase-old" element={<ComponentShowcase />} />

      {/* AI Proctoring Test - Public route for testing TensorFlow.js */}
      <Route path="/ai-proctor-test" element={<AIProctorTest />} />

      {/* Loading Screen Demos */}
      <Route path="/loading/:variant" element={<LoadingScreenDemo />} />

      {/* Coming Soon Pages */}
      <Route path="/coming-soon" element={<ComingSoon />} />
      <Route path="/coming-soon-simple" element={<ComingSoonSimple />} />

      {/* Access Denied */}
      <Route path="/access-denied" element={<AccessDenied />} />

      {/* Server Error */}
      <Route path="/error" element={<ServerError />} />

      {/* 404 - Catch all unmatched routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Main App component wrapped with NexusProvider
//
// v4 notes:
// - No zustand-persist hydration workaround: the v4 SDK holds auth state in
//   memory only, so there is nothing to hydrate from localStorage.
// - No dev-only session-bridging shim: the SDK's NexusProvider calls
//   `/api/v1/auth/refresh` on mount automatically, which reads the
//   HttpOnly refresh-token cookie set by nexus-auth.
function App() {
  // Bypass Nexus for test routes
  if (window.location.pathname === '/ai-proctor-test') {
    return (
      <ErrorBoundary>
        <AIProctorTest />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <NexusProvider
        config={nexusConfig}
        loadingFallback={<LoadingScreen message="Loading..." variant="cyber" />}
      >
        <ErrorHandler>
          <AppRoutes />
        </ErrorHandler>
      </NexusProvider>
    </ErrorBoundary>
  );
}

export default App;
